import type { TileXY } from '../types';

export type IsBlocked = (x: number, y: number) => boolean;

const DIRS: ReadonlyArray<readonly [number, number]> = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

/**
 * Breadth-first shortest path on a 4-connected tile grid (no physics).
 * Returns the path from start (exclusive) to goal (inclusive), [] if already
 * there, or null if unreachable / goal blocked.
 */
export function findPath(
  start: TileXY,
  goal: TileXY,
  width: number,
  height: number,
  isBlocked: IsBlocked,
): TileXY[] | null {
  const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height;
  if (!inBounds(goal.x, goal.y) || isBlocked(goal.x, goal.y)) return null;
  if (start.x === goal.x && start.y === goal.y) return [];

  const key = (x: number, y: number): number => y * width + x;
  const startK = key(start.x, start.y);
  const goalK = key(goal.x, goal.y);

  const visited = new Uint8Array(width * height);
  const cameFrom = new Map<number, number>();
  const queue: number[] = [startK];
  visited[startK] = 1;

  let found = false;
  while (queue.length > 0) {
    const cur = queue.shift() as number;
    if (cur === goalK) {
      found = true;
      break;
    }
    const cx = cur % width;
    const cy = Math.floor(cur / width);
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!inBounds(nx, ny)) continue;
      const nk = key(nx, ny);
      if (visited[nk] || isBlocked(nx, ny)) continue;
      visited[nk] = 1;
      cameFrom.set(nk, cur);
      queue.push(nk);
    }
  }
  if (!found) return null;

  const path: TileXY[] = [];
  let node = goalK;
  while (node !== startK) {
    path.push({ x: node % width, y: Math.floor(node / width) });
    const prev = cameFrom.get(node);
    if (prev === undefined) break;
    node = prev;
  }
  path.reverse();
  return path;
}

/**
 * Nearest walkable tile orthogonally adjacent to a (possibly blocked) target,
 * for walking up to interactables (bed, shipping bin). Picks the one closest to
 * `from` by Manhattan distance.
 */
export function adjacentWalkable(
  target: TileXY,
  from: TileXY,
  width: number,
  height: number,
  isBlocked: IsBlocked,
): TileXY | null {
  const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height;
  let best: TileXY | null = null;
  let bestD = Infinity;
  for (const [dx, dy] of DIRS) {
    const nx = target.x + dx;
    const ny = target.y + dy;
    if (!inBounds(nx, ny) || isBlocked(nx, ny)) continue;
    const d = Math.abs(nx - from.x) + Math.abs(ny - from.y);
    if (d < bestD) {
      bestD = d;
      best = { x: nx, y: ny };
    }
  }
  return best;
}
